import React, { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { ListChecks, Loader2, TrendingUp, TrendingDown, Users, BarChart3, Activity, Sparkles, Crown } from "lucide-react";

const DOT = {
  high: { color: "#ef4444", label: { it: "Alta", en: "High" } },
  medium: { color: "#f59e0b", label: { it: "Media", en: "Medium" } },
  low: { color: "#22c55e", label: { it: "Info", en: "Info" } },
};
const ICON = { price: TrendingUp, pace: Activity, benchmark: BarChart3, competitor: Users, info: Sparkles };

export default function ActionCenter({ propId, lang = "it", onPickDate }) {
  const L = (it, en) => (lang === "en" ? en : it);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!propId) return;
    setLoading(true);
    try { const { data } = await api.get(`/properties/${propId}/action-center?lang=${lang}`); setData(data); }
    catch { setData(null); }
    finally { setLoading(false); }
  }, [propId, lang]);

  useEffect(() => { load(); }, [load]);

  return (
    <motion.div data-testid="action-center" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="md:col-span-4 bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ListChecks className="w-5 h-5 text-primary" />
          <h3 className="text-lg font-semibold">{L("Cosa fare oggi", "What to do today")}</h3>
        </div>
        {data?.count != null && (
          <span className="text-xs px-2.5 py-1 rounded-full bg-muted text-muted-foreground" data-testid="ac-count">
            {data.count} {L("azioni", "actions")}
          </span>
        )}
      </div>

      {loading ? (
        <div className="py-8 text-center text-muted-foreground text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />{L("Analizzo la tua struttura…", "Analyzing your property…")}</div>
      ) : !data?.actions?.length ? (
        <p className="text-sm text-muted-foreground">{L("Nessuna azione al momento.", "No actions right now.")}</p>
      ) : (
        <ul className="space-y-2" data-testid="ac-list">
          {data.actions.map((a, i) => {
            const dot = DOT[a.priority] || DOT.low;
            const Icon = ICON[a.category] || Sparkles;
            const clickable = a.category === "price" && a.meta?.date && onPickDate;
            return (
              <li key={i} data-testid={`ac-item-${i}`}
                onClick={clickable ? () => onPickDate(a.meta.date) : undefined}
                className={`flex items-start gap-3 rounded-lg border border-border/60 p-3 transition-colors ${clickable ? "cursor-pointer hover:bg-accent/30" : ""}`}>
                <span className="mt-1.5 w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: dot.color }} data-testid={`ac-dot-${a.priority}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4 shrink-0" style={{ color: dot.color }} />
                    <p className="text-sm font-medium">{a.title}</p>
                    {a.meta?.leader && (
                      <span data-testid={`ac-leader-badge-${i}`}
                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400 shrink-0">
                        <Crown className="w-3 h-3" />{L("Leader", "Leader")}
                      </span>
                    )}
                  </div>
                  {a.detail && <p className="text-xs text-muted-foreground mt-0.5 ml-6">{a.detail}</p>}
                  {a.meta?.recommended_price != null && (
                    <p className="text-xs mt-1 ml-6 font-data" data-testid={`ac-net-${i}`}>
                      <span className="text-primary font-semibold">{L("Netto", "Net")}: €{a.meta.recommended_price_net != null ? a.meta.recommended_price_net : a.meta.recommended_price}</span>
                      <span className="text-muted-foreground"> · {L("Lordo", "Gross")}: €{a.meta.recommended_price}</span>
                    </p>
                  )}
                </div>
                {clickable && <span className="text-[10px] text-primary self-center shrink-0">{L("Apri", "Open")} →</span>}
              </li>
            );
          })}
        </ul>
      )}
    </motion.div>
  );
}
